import React, { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import { PublicPageLayout } from '../page-shell/public-page-layout';
import { postThirdPartyAccess } from '../../api/access/post-third-party-access';
import { ThirdPartyType } from '../../types';
import { postResendThirdPartyAccess } from '../../api/access/post-resend-third-party-access';
import {
  isGoneError,
  isNotFoundError,
  isTooManyRequestsError,
  isUnauthorizedError,
  GoneErrorCode,
} from '../../utils/errors';

const TYPE_CONFIG: Record<ThirdPartyType, {
  labelKey: string;
  descriptionKey: string;
  route: string;
}> = {
  trade_reference: {
    labelKey: 'trade_reference_label',
    descriptionKey: 'trade_reference_access_description',
    route: 'trade-references',
  },
  personal_guarantor: {
    labelKey: 'personal_guarantor_label',
    descriptionKey: 'personal_guarantor_access_description',
    route: 'personal-guarantors',
  },
};

const CODE_EXPIRED_CODES = new Set<GoneErrorCode>([ GoneErrorCode.TradeReferenceCodeExpired, GoneErrorCode.PersonalGuarantorCodeExpired ]);

const CODE_EXHAUSTED_CODES = new Set<GoneErrorCode>([ GoneErrorCode.TradeReferenceCodeExhausted, GoneErrorCode.PersonalGuarantorCodeExhausted ]);

const ALREADY_SUBMITTED_CODES = new Set<GoneErrorCode>([ GoneErrorCode.TradeReferenceAlreadySubmitted, GoneErrorCode.PersonalGuarantorAlreadySubmitted ]);

type PageState =
  | { type: 'select-type' }
  | { type: 'form'; thirdPartyType: ThirdPartyType }
  | { type: 'error'; message: string; canResend: boolean; thirdPartyType: ThirdPartyType }
  | { type: 'already-submitted' }
  | { type: 'resend-success' }
  | { type: 'resend-error'; message: string; thirdPartyType: ThirdPartyType };

const ThirdPartyAccess: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('components', { keyPrefix: 'pages' });
  const [ code, setCode ] = useState('');
  const [ email, setEmail ] = useState('');
  const [ loading, setLoading ] = useState(false);
  const [ pageState, setPageState ] = useState<PageState>({ type: 'select-type' });

  const handleSelectType = (thirdPartyType: ThirdPartyType) => {
    setCode('');
    setEmail('');
    setPageState({
      type: 'form',
      thirdPartyType,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pageState.type !== 'form') return;
    setLoading(true);

    const { thirdPartyType } = pageState;

    try {
      const result = await postThirdPartyAccess(code, email, thirdPartyType);
      navigate({ to: `/${TYPE_CONFIG[thirdPartyType].route}/${result.urlSlug}/${result.trackingId}` });
    } catch (err) {
      if (isGoneError(err)) {
        if (err.code && ALREADY_SUBMITTED_CODES.has(err.code as GoneErrorCode)) {
          setPageState({ type: 'already-submitted' });
        } else if (
          err.code && (CODE_EXPIRED_CODES.has(err.code as GoneErrorCode) ||
          CODE_EXHAUSTED_CODES.has(err.code as GoneErrorCode))
        ) {
          setPageState({
            type: 'error',
            message: err.message,
            canResend: true,
            thirdPartyType,
          });
        } else {
          setPageState({
            type: 'error',
            message: err.message,
            canResend: false,
            thirdPartyType,
          });
        }
      } else if (isNotFoundError(err)) {
        setPageState({
          type: 'error',
          message: t('no_record_found'),
          canResend: false,
          thirdPartyType,
        });
      } else if (isUnauthorizedError(err)) {
        setPageState({
          type: 'error',
          message: err.message,
          canResend: false,
          thirdPartyType,
        });
      } else {
        setPageState({
          type: 'error',
          message: t('generic_error'),
          canResend: false,
          thirdPartyType,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (pageState.type !== 'error' || !pageState.canResend) return;
    const { thirdPartyType } = pageState;
    setLoading(true);

    try {
      await postResendThirdPartyAccess(email, thirdPartyType);
      setPageState({ type: 'resend-success' });
    } catch (err) {
      if (isGoneError(err) && err.code && ALREADY_SUBMITTED_CODES.has(err.code as GoneErrorCode)) {
        setPageState({ type: 'already-submitted' });
      } else if (isTooManyRequestsError(err)) {
        setPageState({
          type: 'resend-error',
          message: t('resend_rate_limited'),
          thirdPartyType,
        });
      } else {
        setPageState({
          type: 'resend-error',
          message: isGoneError(err) || isNotFoundError(err)
            ? err.message
            : t('generic_error'),
          thirdPartyType,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (pageState.type === 'already-submitted') {
    return (
      <PublicPageLayout showFooter>
        <Box
          sx={{
            px: 5,
            py: 6,
            textAlign: 'center',
          }}
        >
          <Typography
            gutterBottom
            fontWeight={700}
            variant='h5'
          >
            {t('already_submitted')}
          </Typography>
          <Typography
            color='text.secondary'
            variant='body1'
          >
            {t('already_submitted_detail')}
          </Typography>
        </Box>
      </PublicPageLayout>
    );
  }

  if (pageState.type === 'resend-success') {
    return (
      <PublicPageLayout showFooter>
        <Box
          sx={{
            px: 5,
            py: 6,
          }}
        >
          <Typography
            gutterBottom
            fontWeight={700}
            variant='h5'
          >
            {t('new_code_sent')}
          </Typography>
          <Typography
            color='text.secondary'
            sx={{ mb: 3 }}
            variant='body1'
          >
            <Trans
              components={{ bold: <strong /> }}
              i18nKey='components:pages.new_code_sent_message'
              values={{ email }}
            />
          </Typography>
          <Button
            variant='outlined'
            onClick={() => setPageState({ type: 'select-type' })}
          >
            {t('back_to_start')}
          </Button>
        </Box>
      </PublicPageLayout>
    );
  }

  if (pageState.type === 'select-type') {
    return (
      <PublicPageLayout showFooter>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            px: 5,
            py: 4,
          }}
        >
          <Typography
            color='white'
            fontWeight={700}
            variant='h5'
          >
            {t('third_party_access_title')}
          </Typography>
          <Typography
            sx={{
              color: 'white',
              mt: 0.5,
              fontWeight: 700,
            }}
            variant='body2'
          >
            {t('third_party_access_subtitle')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            px: 5,
            py: 4,
          }}
        >
          {(Object.entries(TYPE_CONFIG) as [ ThirdPartyType, typeof TYPE_CONFIG[ThirdPartyType] ][]).map(([ value, config ]) => (
            <Card
              key={value}
              sx={{ borderRadius: 2 }}
              variant='outlined'
            >
              <CardActionArea
                sx={{
                  p: 2.5,
                }}
                onClick={() => handleSelectType(value)}
              >
                <Typography fontWeight={700}>
                  {t(config.labelKey)}
                </Typography>
                <Typography
                  color='text.secondary'
                  sx={{ mt: 0.5 }}
                  variant='body2'
                >
                  {t(config.descriptionKey)}
                </Typography>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      </PublicPageLayout>
    );
  }

  const thirdPartyType = pageState.type === 'form' || pageState.type === 'error' || pageState.type === 'resend-error'
    ? pageState.thirdPartyType
    : 'trade_reference' as ThirdPartyType;

  return (
    <PublicPageLayout showFooter>
      <Box
        sx={{
          backgroundColor: 'primary.main',
          px: 5,
          py: 4,
        }}
      >
        <Typography
          color='white'
          fontWeight={700}
          variant='h5'
        >
          {t(TYPE_CONFIG[thirdPartyType].labelKey)}
        </Typography>
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.8)',
            mt: 0.5,
          }}
          variant='body2'
        >
          {t('form_entry_subtitle')}
        </Typography>
      </Box>

      <Box
        component='form'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          px: 5,
          py: 4,
        }}
        onSubmit={handleSubmit}
      >
        <TextField
          fullWidth
          required
          autoComplete='email'
          label={t('email_address_label')}
          type='email'
          value={email}
          onChange={(e) => {
            setEmail(e.target.value); setPageState({
              type: 'form',
              thirdPartyType,
            });
          }}
        />
        <TextField
          fullWidth
          required
          inputProps={{
            inputMode: 'numeric',
            maxLength: 6,
          }}
          label={t('access_code_label')}
          placeholder={t('access_code_placeholder')}
          value={code}
          onChange={(e) => {
            setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setPageState({
              type: 'form',
              thirdPartyType,
            });
          }}
        />

        {(pageState.type === 'error' || pageState.type === 'resend-error') && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography
              color='error'
              variant='body2'
            >
              {pageState.message}
            </Typography>
            {pageState.type === 'error' && pageState.canResend && (
              <Button
                disabled={loading || !email}
                size='small'
                sx={{
                  alignSelf: 'center',
                  px: 2,
                }}
                variant='text'
                onClick={handleResend}
              >
                {loading ? <CircularProgress size={16} /> : t('send_new_code')}
              </Button>
            )}
          </Box>
        )}

        <Button
          fullWidth
          disabled={loading || code.length !== 6 || !email || pageState.type === 'error' || pageState.type === 'resend-error'}
          size='large'
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            mt: 0.5,
            py: 1.5,
          }}
          type='submit'
          variant='contained'
        >
          {loading
            ? (
              <CircularProgress
                color='inherit'
                size={24}
              />
              )
            : t('access_form')}
        </Button>

        <Button
          color='inherit'
          size='small'
          sx={{
            alignSelf: 'flex-start',
            px: 0,
          }}
          variant='text'
          onClick={() => setPageState({ type: 'select-type' })}
        >
          {t('back')}
        </Button>
      </Box>
    </PublicPageLayout>
  );
};

export default ThirdPartyAccess;
